// Admin CMS for the customer-facing promo banner carousel. Supports:
//   • Upload (JPG/PNG/WEBP, ≤8MB) with optional title + deep link
//   • Toggle active / inactive
//   • Reorder via up/down arrows
//   • Delete (also removes the storage blob)
import { useEffect, useRef, useState } from "react";
import api, { extractError } from "@/api/client";
import { toast } from "sonner";
import { X, Upload, Image as ImageIcon, Trash2, Eye, EyeOff, ArrowUp, ArrowDown, Link as LinkIcon } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminBannersModal({ onClose }) {
  const [banners, setBanners] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: "", link: "" });

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/admin/banners");
      setBanners(data.banners || []);
    } catch (e) { setErr(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { setErr("اختر صورة JPG/PNG/WEBP"); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("title", form.title);
    fd.append("link", form.link);
    fd.append("sort_order", String(banners.length));
    fd.append("active", "true");
    setBusy(true); setErr("");
    try {
      await api.post("/admin/banners", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("✓ تم رفع البانر", { duration: 2500 });
      fileRef.current.value = "";
      setForm({ title: "", link: "" });
      await load();
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const patch = async (id, updates) => {
    try { await api.patch(`/admin/banners/${id}`, updates); await load(); }
    catch (e) { setErr(extractError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف هذا البانر نهائياً؟")) return;
    try { await api.delete(`/admin/banners/${id}`); toast.success("✓ حُذف البانر", { duration: 2000 }); await load(); }
    catch (e) { setErr(extractError(e)); }
  };

  const move = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= banners.length) return;
    const next = [...banners];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setBanners(next);
    try { await api.post("/admin/banners/reorder", { ids: next.map((b) => b.id) }); }
    catch (e) { setErr(extractError(e)); load(); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="banners-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-3xl rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex justify-between items-center p-5 border-b border-[var(--border)] z-10">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary-brand text-white flex items-center justify-center"><ImageIcon size={18}/></div>
            <div>
              <h2 className="text-lg font-black" style={{fontFamily:'Cairo'}}>البانرات الإعلانية</h2>
              <div className="text-[11px] text-[var(--text-secondary)]">تظهر أعلى صفحة المحلات · تلقائية التدوير</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center" data-testid="banners-close">
            <X size={16}/>
          </button>
        </div>

        {err && <div className="mx-5 mt-3 bg-red-50 text-red-600 px-3 py-2 rounded-lg text-sm" data-testid="banners-err">{err}</div>}

        <div className="p-5 space-y-4">
          {/* Upload form */}
          <div className="rounded-2xl border border-dashed border-primary-brand bg-primary-light/50 p-4 space-y-3">
            <div className="font-black text-sm flex items-center gap-2">
              <Upload size={16} className="text-primary-brand"/> إضافة بانر جديد
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
              className="block w-full text-xs file:ml-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-primary-brand file:text-white file:font-bold"
              data-testid="banner-file"/>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="عنوان (اختياري)" maxLength={80}
              className="w-full text-sm px-3 py-2 rounded-lg border border-[var(--border)] bg-white"
              data-testid="banner-title"/>
            <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })}
              placeholder="رابط عند الضغط (اختياري): /r/burger-house أو https://..."
              className="w-full text-sm px-3 py-2 rounded-lg border border-[var(--border)] bg-white" dir="ltr"
              data-testid="banner-link"/>
            <button onClick={upload} disabled={busy}
              className="w-full py-2.5 rounded-xl bg-primary-brand text-white font-black text-sm hover:opacity-90 disabled:opacity-60"
              data-testid="btn-upload-banner">
              {busy ? "جارٍ الرفع..." : "رفع البانر"}
            </button>
          </div>

          {/* List */}
          {banners.length === 0 ? (
            <div className="text-center py-10 text-sm text-[var(--text-secondary)]">لا توجد بانرات — ارفع أول بانر لعرضه للزبائن.</div>
          ) : (
            <div className="space-y-2">
              {banners.map((b, i) => (
                <div key={b.id} className={`flex items-center gap-3 border rounded-2xl p-3 ${b.active ? "bg-white border-[var(--border)]" : "bg-[var(--surface-alt)] border-dashed opacity-70"}`} data-testid={`banner-row-${b.id}`}>
                  <img src={`${API}${b.image_url}`} alt={b.title || "banner"} className="w-24 h-14 rounded-lg object-cover shrink-0 border border-[var(--border)]"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{b.title || <span className="text-[var(--text-secondary)] italic">بدون عنوان</span>}</div>
                    <div className="text-[11px] text-[var(--text-secondary)] truncate flex items-center gap-1" dir="ltr">
                      {b.link ? <><LinkIcon size={10}/> {b.link}</> : <span dir="rtl" className="italic">بدون رابط</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => move(i, -1)} disabled={i === 0} title="أعلى"
                      className="w-7 h-7 rounded-lg bg-white border border-[var(--border)] hover:border-primary-brand disabled:opacity-40 flex items-center justify-center"
                      data-testid={`banner-up-${b.id}`}>
                      <ArrowUp size={12}/>
                    </button>
                    <button onClick={() => move(i, 1)} disabled={i === banners.length - 1} title="أسفل"
                      className="w-7 h-7 rounded-lg bg-white border border-[var(--border)] hover:border-primary-brand disabled:opacity-40 flex items-center justify-center"
                      data-testid={`banner-down-${b.id}`}>
                      <ArrowDown size={12}/>
                    </button>
                    <button onClick={() => patch(b.id, { active: !b.active })} title={b.active ? "إخفاء" : "إظهار"}
                      className={`w-7 h-7 rounded-lg flex items-center justify-center ${b.active ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100" : "bg-amber-50 text-amber-600 hover:bg-amber-100"}`}
                      data-testid={`banner-toggle-${b.id}`}>
                      {b.active ? <Eye size={12}/> : <EyeOff size={12}/>}
                    </button>
                    <button onClick={() => remove(b.id)} title="حذف"
                      className="w-7 h-7 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 flex items-center justify-center"
                      data-testid={`banner-delete-${b.id}`}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
